import { useQuery } from '@tanstack/react-query';
import { featureFlags, type EffectiveFeatureRow, type EffectiveResponse } from '@/api/featureFlags';
import { useAuth } from '@/lib/auth-store';
import { useCustomerContext } from '@/lib/customer-context-store';

const QUERY_KEY = ['feature-flags', 'effective'] as const;
const STALE_MS = 60_000;

/**
 * Fetches the current user's effective feature flags and returns an
 * `isEnabled(key)` helper. One cached query (staleTime 60s).
 *
 * Super-admin bypass: normally super-admin sees every feature (Sam's
 * guardrail — they manage everything and shouldn't be gated). BUT when a
 * super-admin / reseller_owner has "entered" a customer (customer-context
 * store), we PREVIEW that customer's real flags instead of bypassing — so
 * turning a feature off for a customer is actually visible while you're
 * operating inside that customer. Both ids are passed so the
 * customer→reseller→default resolution is correct. Exit the customer to go
 * back to the see-everything view.
 *
 * Unknown keys default to `false` (fail closed) once data has loaded.
 */
export function useFeatureFlag() {
  const user = useAuth((s) => s.user);
  const entered = useCustomerContext((s) => s.current);
  const previewing = Boolean(entered);

  const query = useQuery<EffectiveResponse>({
    queryKey: [...QUERY_KEY, entered?.id ?? 'self'],
    queryFn: () =>
      featureFlags.effective(
        entered ? { customer_id: entered.id, reseller_id: entered.resellerId } : {},
      ),
    staleTime: STALE_MS,
    enabled: Boolean(user),
  });

  const rows: EffectiveFeatureRow[] = query.data?.rows ?? [];
  const map = new Map(rows.map((r) => [r.key, r]));

  function isEnabled(key: string): boolean {
    // Super admin sees everything — EXCEPT while previewing an entered
    // customer, where we reflect that customer's real flags.
    if (user?.is_super_admin && !previewing) return true;
    // While loading, treat unknown as true to avoid flash-of-empty-nav;
    // once data lands the real value takes over.
    if (query.isLoading || query.isPending) return true;
    const row = map.get(key);
    return row?.enabled ?? false;
  }

  return {
    isEnabled,
    features: rows,
    isLoading: query.isLoading,
    refetch: query.refetch,
    /** True when reflecting an entered customer's flags rather than the caller's own scope. */
    previewing,
  };
}

export { QUERY_KEY as FEATURE_FLAGS_QUERY_KEY };
